import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, queryKeys, Notification } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useEffect } from 'react';
import { authenticatedFetch } from '../utils/apiUtils';

interface NotificationsResponse {
  notifications: Notification[];
  unreadCount: number;
}

export function useNotifications() {
  const { isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.notifications.all,
    queryFn: apiClient.getNotifications,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: isAuthenticated, // Only fetch if user is authenticated
    retry: false, // Don't retry on failure to prevent loops
  });
}

export function useNotificationSSE() {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;

    let eventSource: EventSource | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 3;
    const RECONNECT_DELAY = 5000;

    const connect = async () => {
      if (eventSource) {
        eventSource.close();
      }

      try {
        const tokenResponse = await authenticatedFetch(`${import.meta.env.VITE_API_URL}/auth/sse-token`, {
          method: 'POST',
        });

        if (!tokenResponse.ok) {
          console.error('Failed to get SSE token');
          return;
        }

        const { sseToken } = await tokenResponse.json();
        eventSource = new EventSource(`${import.meta.env.VITE_API_URL}/notifications/sse?token=${sseToken}`);

        eventSource.onmessage = (event) => {
          try {
            const newNotification = JSON.parse(event.data) as Notification;

            queryClient.setQueryData<NotificationsResponse>(queryKeys.notifications.all, (old) => {
              if (!old) {
                return {
                  notifications: [newNotification],
                  unreadCount: newNotification.read ? 0 : 1,
                };
              }

              const currentNotifications = Array.isArray(old.notifications) ? old.notifications : [];
              const alreadyExists = currentNotifications.some((n) => n._id === newNotification._id);
              if (alreadyExists) {
                return old;
              }

              const currentUnreadCount = typeof old.unreadCount === 'number' ? old.unreadCount : 0;
              return {
                notifications: [newNotification, ...currentNotifications],
                unreadCount: newNotification.read ? currentUnreadCount : currentUnreadCount + 1,
              };
            });

            reconnectAttempts = 0;
          } catch (error) {
            console.error('Error parsing SSE message:', error);
          }
        };

        eventSource.onerror = (error) => {
          console.error('SSE Error:', error);
          if (eventSource) {
            eventSource.close();
            eventSource = null;
          }

          if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
            reconnectAttempts++;
            reconnectTimeout = setTimeout(connect, RECONNECT_DELAY);
          }
        };

        eventSource.onopen = () => {
          reconnectAttempts = 0;
        };
      } catch (error) {
        console.error('Error setting up SSE connection:', error);
      }
    };

    connect();

    return () => {
      if (eventSource) {
        eventSource.close();
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, [queryClient, isAuthenticated]);
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: apiClient.markNotificationsAsRead,
    onSuccess: () => {
      // Update notifications in cache to mark them as read
      queryClient.setQueryData(queryKeys.notifications.all, (old: any) => {
        if (!old) return old;
        const updatedNotifications = old.notifications.map((n: any) => ({
          ...n,
          read: true,
        }));
        return {
          ...old,
          notifications: updatedNotifications,
          unreadCount: 0, // All notifications are now read
        };
      });
    },
    onError: (error) => {
      console.error('Error marking notifications as read:', error);
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: apiClient.deleteNotification,
    onSuccess: (_, notificationId) => {
      // Remove the deleted notification from the cache
      queryClient.setQueryData(queryKeys.notifications.all, (old: any) => {
        if (!old) return old;
        const updatedNotifications = old.notifications.filter((n: any) => n._id !== notificationId);
        const updatedUnreadCount = updatedNotifications.filter((n: any) => !n.read).length;
        return {
          ...old,
          notifications: updatedNotifications,
          unreadCount: updatedUnreadCount,
        };
      });
    },
    onError: (error: any) => {
      console.error('Error deleting notification:', error);
      if (error.response?.status === 404) {
        console.error('Notification not found - it may have been deleted already or does not exist');
      }
    },
  });
} 